import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";

/**
 * Simple canvas-based signature pad. Exposes { clear, isEmpty, toDataURL } via ref.
 * Uses Pointer Events so it works with mouse, touch and stylus.
 */
const SignaturePad = forwardRef(function SignaturePad(
  { width = 480, height = 160, penColor = "#0A0A0A", className = "" },
  ref,
) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = 2;
    ctx.strokeStyle = penColor;
    // white background so exported PNG isn't transparent when placed on PDFs
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
  }, [width, height, penColor]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    last.current = getPos(e);
  };

  const onPointerMove = (e) => {
    if (!drawing.current) return;
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    dirty.current = true;
  };

  const onPointerUp = () => { drawing.current = false; last.current = null; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    dirty.current = false;
  };

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => !dirty.current,
    toDataURL: () => (dirty.current ? canvasRef.current.toDataURL("image/png") : null),
  }));

  return (
    <div className={`relative border border-neutral-900 bg-white ${className}`}>
      <canvas
        ref={canvasRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        className="touch-none cursor-crosshair block"
        data-testid="signature-canvas"
      />
      <button
        type="button"
        onClick={clear}
        data-testid="signature-clear"
        className="absolute top-2 right-2 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border border-neutral-300 bg-white hover:bg-neutral-900 hover:text-white"
      >
        Clear
      </button>
    </div>
  );
});

export default SignaturePad;
